import { useCallback, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useCart from 'hooks/use-cart';
import useUI from 'hooks/use-ui';
import { cartItemId } from 'utils/helpers/product';
import { consumerDispensaries } from 'shared/graphql/dispensary/queries';
import { GqlDispensaries } from 'types/graphql';
import { useReorderUnavailableItemsModal } from 'components/modals/reorder-unavailable-items';
import { ConsumerOrder } from 'src/components/embedded-account/account.types';
import { useReorderItems, ReorderAvailableItem } from '../use-reorder-items';

export const REORDER_FLAG = 'ecomm.one-click-reording.rollout';

type UseReorderReturn = {
  reorder: ((order: ConsumerOrder) => Promise<void>) | undefined;
  loadingOrderId: string | null;
};

export function useReorder(): UseReorderReturn {
  const flags = useFlags();
  const isEnabled: boolean = flags[REORDER_FLAG] ?? false;
  const apolloClient = useApolloClient();
  const Cart = useCart();
  const UI = useUI();
  const { fetchReorderItems } = useReorderItems();
  const unavailableItemsModal = useReorderUnavailableItemsModal();
  const [loadingOrderId, setLoadingOrderId] = useState<string | null>(null);
  const reorderingRef = useRef(false);

  const reorder = useCallback(
    async (order: ConsumerOrder): Promise<void> => {
      const dispensaryId = order.dispensary?._id;
      const { orderId } = order;
      if (!dispensaryId || !order.orders.length || !orderId) {
        return;
      }

      if (reorderingRef.current) {
        return;
      }
      reorderingRef.current = true;
      setLoadingOrderId(orderId);
      try {
        // Fetch dispensary and product availability in parallel.
        // fetchReorderItems only needs the dispensaryId string, not the full
        // dispensary object, so these two queries are independent.
        const [dispoResult, { availableItems, unavailableItems }] = await Promise.all([
          apolloClient.query({
            query: consumerDispensaries,
            variables: { dispensaryFilter: { cNameOrID: dispensaryId } },
          }),
          fetchReorderItems(order.orders, dispensaryId, UI.isKiosk),
        ]);

        // We still need the full dispensary object for Cart.addItem.
        const fetchedDispensary = dispoResult?.data?.filteredDispensaries?.[0];
        if (!fetchedDispensary) {
          return;
        }

        if (availableItems.length === 0) {
          if (unavailableItems.length > 0) {
            await unavailableItemsModal.open({ unavailableItems });
          }
          return;
        }

        addItemsToCart(availableItems, Cart, fetchedDispensary);

        if (unavailableItems.length > 0) {
          await unavailableItemsModal.open({ unavailableItems });
        }

        UI.openCartDrawer();
      } catch (err) {
        // TODO: surface error to user (e.g. toast notification) ECOMM-8581
        console.error('[useReorder] reorder failed', err);
      } finally {
        reorderingRef.current = false;
        setLoadingOrderId(null);
      }
    },
    [Cart, UI, apolloClient, fetchReorderItems, unavailableItemsModal]
  );

  if (!isEnabled) {
    return { reorder: undefined, loadingOrderId: null };
  }

  return { reorder, loadingOrderId };
}

function addItemsToCart(
  items: ReorderAvailableItem[],
  cart: ReturnType<typeof useCart>,
  dispensary: GqlDispensaries
): void {
  items.forEach((item) => {
    const cartItem = {
      product: item.product,
      option: item.option,
      quantity: item.quantity,
      additionalOption: item.additionalOption,
    };
    cart.addItem(dispensary, cartItem, cartItemId(cartItem));
  });
}
